/**
 * Admin Ranking Policy Editor  (Phase P5 · Step 5D · Cleaned in P6B)
 * ==================================================================
 * FRONTEND-ONLY design contract.
 *
 * - No backend calls.
 * - No fake providers, no fake impact, no fake publish, no fake versions.
 * - This page is a DESIGN CONTRACT surface only. It is NOT connected to
 *   live ranking. A truthful banner at the top makes this explicit.
 *
 * What remains on this page (all truthful):
 *   · Available Signals (fixed catalog — design contract)
 *   · Selected Signals & Weights (local UI state, sums to 100)
 *   · Validation constraints (UI-enforced)
 *   · Gate Rules (static system rules — not editable)
 *   · Weight Distribution (derived from current UI inputs only)
 *   · Reset to Default (pure local reset)
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Shield, Zap, Activity, Lock, X,
  AlertTriangle, CheckCircle2, Info, Plus, RotateCcw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

/* ════════════════════════════════════════════════════════════
   FIXED SYSTEM CATALOG — not editable, not free-text.
   Admin may only SELECT from this list.
   ═══════════════════════════════════════════════════════════ */
const SIGNAL_CATALOG = [
  // CORE
  { key: 'verification_level',             name: 'Verification Level',             type: 'CORE',        desc: 'Overall verification tier (verified / partial / unverified).',           source: 'provider_compliance_summary.verification.overall_status' },
  { key: 'profile_completeness',           name: 'Profile Completeness',           type: 'CORE',        desc: 'Share of required identity, contact, billing and media fields filled.', source: 'providers.* required fields fill ratio' },
  { key: 'treatment_content_completeness', name: 'Treatment Content Completeness', type: 'CORE',        desc: 'Depth of treatment pages: description, FAQs, media, price ranges.',     source: 'treatment_content.* per provider' },
  // BOOST
  { key: 'active_campaign_boost',          name: 'Active Campaign Boost',          type: 'BOOST',       desc: 'Boost while provider has ≥1 active campaign.',                           source: 'clinic_treatment_campaigns.status=active' },
  { key: 'positioning_tag_boost',          name: 'Positioning Tag Boost',          type: 'BOOST',       desc: 'Admin-flagged premium positioning tags (e.g. Premium Boutique).',        source: 'positioning_tag_catalog.is_premium_signal' },
  { key: 'vip_services_boost',             name: 'VIP Services Boost',             type: 'BOOST',       desc: 'Admin-flagged premium VIP services (e.g. Private Jet).',                 source: 'vip_service_catalog.is_premium_signal' },
  // OPERATIONAL
  { key: 'response_time',                  name: 'Response Time',                  type: 'OPERATIONAL', desc: 'Rolling median response time to patient quote requests.',                source: 'quotes.* response time aggregate' },
  { key: 'trust_index',                    name: 'Trust Index',                    type: 'OPERATIONAL', desc: 'Composite of accreditations, doctor verification and certifications.',  source: 'Derived from doctor profiles + accreditations' },
  { key: 'activity_freshness',             name: 'Activity / Freshness',           type: 'OPERATIONAL', desc: 'Recency of profile updates, active campaigns and content edits.',       source: 'providers.updated_at_utc + campaign freshness' },
];

const TYPE_STYLE = {
  CORE:        { chip: 'bg-violet-50 text-violet-700 border border-violet-200',       icon: Shield,   accent: 'violet'  },
  BOOST:       { chip: 'bg-amber-50 text-amber-700 border border-amber-200',          icon: Zap,      accent: 'amber'   },
  OPERATIONAL: { chip: 'bg-slate-100 text-slate-600 border border-slate-200',         icon: Activity, accent: 'slate'   },
};

/* Default weights for the design contract. Sums to 100. */
const DEFAULT_POLICY = [
  { key: 'verification_level',              weight: 30 },
  { key: 'profile_completeness',            weight: 15 },
  { key: 'treatment_content_completeness',  weight: 10 },
  { key: 'active_campaign_boost',           weight: 10 },
  { key: 'positioning_tag_boost',           weight: 5 },
  { key: 'vip_services_boost',              weight: 5 },
  { key: 'trust_index',                     weight: 15 },
  { key: 'response_time',                   weight: 5 },
  { key: 'activity_freshness',              weight: 5 },
];

/* Locked system gate rules (explainability only — not editable). */
const GATE_RULES = [
  { key: 'g_active',    title: 'Provider must be active',                           label: 'System Controlled', desc: 'Archived or suspended providers never appear in results.' },
  { key: 'g_public',    title: 'Provider must be searchable / public',              label: 'Locked Rule',       desc: 'Private profiles, drafts and hidden providers are excluded.' },
  { key: 'g_treatment', title: 'Provider must match selected treatment / category', label: 'System Controlled', desc: 'Gate is applied before any weight-based ranking runs.' },
  { key: 'g_verified',  title: 'Provider must pass minimum verification',           label: 'Locked Rule',       desc: 'Providers with zero verified critical fields are filtered out of default search.' },
];

const LIMITS = {
  CORE_MIN:        50,
  BOOST_MAX:       30,
  OPERATIONAL_MAX: 30,
};

/* ════════════════════════════════════════════════════════════ */

export default function AdminRankingPolicy() {
  const navigate = useNavigate();

  const [selected, setSelected] = useState(() => [...DEFAULT_POLICY]);

  const catalogByKey = useMemo(
    () => Object.fromEntries(SIGNAL_CATALOG.map(s => [s.key, s])),
    []
  );
  const selectedKeys = useMemo(() => new Set(selected.map(s => s.key)), [selected]);

  const totals = useMemo(() => {
    const t = { CORE: 0, BOOST: 0, OPERATIONAL: 0, ALL: 0 };
    for (const s of selected) {
      const meta = catalogByKey[s.key];
      if (!meta) continue;
      t[meta.type] += Number(s.weight) || 0;
      t.ALL += Number(s.weight) || 0;
    }
    return t;
  }, [selected, catalogByKey]);

  const coreSelectedCount = selected.filter(s => catalogByKey[s.key]?.type === 'CORE').length;

  const violations = useMemo(() => {
    const v = [];
    if (totals.ALL !== 100)                              v.push(`Total must equal 100 (now ${totals.ALL}).`);
    if (totals.CORE < LIMITS.CORE_MIN)                   v.push(`CORE total must be ≥ ${LIMITS.CORE_MIN} (now ${totals.CORE}).`);
    if (totals.BOOST > LIMITS.BOOST_MAX)                 v.push(`BOOST total must be ≤ ${LIMITS.BOOST_MAX} (now ${totals.BOOST}).`);
    if (totals.OPERATIONAL > LIMITS.OPERATIONAL_MAX)     v.push(`OPERATIONAL total must be ≤ ${LIMITS.OPERATIONAL_MAX} (now ${totals.OPERATIONAL}).`);
    if (coreSelectedCount === 0)                         v.push(`At least one CORE signal is required.`);
    return v;
  }, [totals, coreSelectedCount]);

  const isValid = violations.length === 0;

  /* ── Actions (LOCAL ONLY — no backend, no publish) ──────── */
  const addSignal = (key) => {
    if (selectedKeys.has(key)) return;
    setSelected(prev => [...prev, { key, weight: 0 }]);
  };

  const removeSignal = (key) => {
    const meta = catalogByKey[key];
    if (meta?.type === 'CORE' && coreSelectedCount <= 1) {
      toast.error('At least one CORE signal is required.');
      return;
    }
    setSelected(prev => prev.filter(s => s.key !== key));
  };

  const setWeight = (key, next) => {
    const clamped = Math.max(0, Math.min(100, Math.round(Number(next) || 0)));
    setSelected(prev => prev.map(s => s.key === key ? { ...s, weight: clamped } : s));
  };

  const resetDefault = () => {
    setSelected([...DEFAULT_POLICY]);
    toast.success('Local weights reset to default');
  };

  /* ── Render ───────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-slate-50" data-testid="ranking-policy-page">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3">

        {/* ── Top Bar ─────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="rp-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-0.5" /> Back
            </Button>
            <h1 className="text-base font-semibold text-[#122033] truncate" data-testid="rp-title">Ranking Policy Management</h1>
          </div>
          <Badge
            className="text-[10px] bg-slate-100 text-slate-600 border border-slate-300"
            data-testid="rp-contract-badge"
          >
            Design contract only
          </Badge>
        </div>

        {/* ── Truthful disclosure strip (REPLACES previous fake Draft/Active) ── */}
        <div
          className="mb-2 flex items-center gap-2 px-2 py-1 rounded bg-slate-100 border border-slate-200 text-[10.5px] text-slate-600"
          data-testid="rp-disclosure"
        >
          <Info className="h-3 w-3 flex-shrink-0 text-slate-500" />
          <span>
            <span className="font-semibold text-slate-700">Design contract only — not connected to live ranking.</span>
            <span className="text-slate-500"> Weights, constraints and signals below describe the intended contract; no ranking backend consumes them yet.</span>
          </span>
        </div>

        {/* ── Violations banner (compact) ───────────────────── */}
        {!isValid && (
          <div className="mb-2 flex items-start gap-1.5 px-2 py-1 rounded bg-red-50 border border-red-200" data-testid="rp-violations">
            <AlertTriangle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-[10.5px] text-red-700 leading-snug flex-1">
              {violations.map((v, i) => <span key={i} className="mr-3">• {v}</span>)}
            </div>
          </div>
        )}

        {/* ── Layout grid: [Catalog | Selected+Weights] ───── */}
        <div className="grid grid-cols-12 gap-2">

          {/* Section 1 — Available Signals */}
          <section className="col-span-12 lg:col-span-4 bg-white border border-slate-200 rounded-md" data-testid="rp-catalog">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/40">
              <h2 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Available Signals (left · catalog)</h2>
              <span className="text-[9px] text-slate-400">{SIGNAL_CATALOG.length} system-defined</span>
            </div>
            <div className="divide-y divide-slate-100">
              {['CORE', 'BOOST', 'OPERATIONAL'].map(group => (
                <div key={group}>
                  <div className="px-2.5 py-0.5 bg-slate-50/70 flex items-center justify-between">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">{group}</span>
                    <span className="text-[9px] text-slate-400">
                      {SIGNAL_CATALOG.filter(s => s.type === group && selectedKeys.has(s.key)).length}/{SIGNAL_CATALOG.filter(s => s.type === group).length} in model
                    </span>
                  </div>
                  {SIGNAL_CATALOG.filter(s => s.type === group).map(sig => {
                    const Icon = TYPE_STYLE[sig.type].icon;
                    const added = selectedKeys.has(sig.key);
                    return (
                      <div
                        key={sig.key}
                        className={`px-2.5 py-1 flex items-start gap-1.5 transition-colors ${added ? 'bg-violet-50/40 opacity-70' : 'hover:bg-slate-50/60'}`}
                        data-testid={`rp-catalog-${sig.key}`}
                      >
                        <Icon className="h-3 w-3 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[11.5px] font-medium truncate ${added ? 'text-slate-500' : 'text-[#122033]'}`}>{sig.name}</span>
                            <span className={`text-[9px] px-1 py-0 rounded ${TYPE_STYLE[sig.type].chip}`}>{sig.type}</span>
                            {added && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`rp-selected-chip-${sig.key}`}>
                                <CheckCircle2 className="h-2.5 w-2.5" /> In model
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{sig.desc}</p>
                          <p className="text-[9px] text-slate-400 leading-snug mt-0.5 font-mono truncate" title={sig.source}>
                            Source: {sig.source}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className={`h-5 px-1.5 text-[10px] ${added ? 'text-emerald-600' : ''}`}
                          disabled={added}
                          onClick={() => addSignal(sig.key)}
                          data-testid={`rp-add-${sig.key}`}
                        >
                          {added ? <CheckCircle2 className="h-3 w-3" /> : <><Plus className="h-3 w-3 mr-0.5" />Add</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="px-2.5 py-1 bg-slate-50/60 border-t border-slate-100 flex items-start gap-1">
              <Info className="h-2.5 w-2.5 text-slate-400 mt-0.5" />
              <span className="text-[9px] text-slate-500 leading-snug">
                System-defined catalog. Admin can only select from this list — no create / rename / redefine.
              </span>
            </div>
          </section>

          {/* Section 2 — Selected Signals & Weights */}
          <section className="col-span-12 lg:col-span-8 bg-white border border-slate-200 rounded-md" data-testid="rp-selected">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-violet-50/30">
              <h2 className="text-[10px] font-semibold text-violet-700 uppercase tracking-wider">Selected Signals &amp; Weights (right · contract)</h2>
              <div className="flex items-center gap-2 text-[10px]">
                <GroupPill label="CORE"        value={totals.CORE}        ok={totals.CORE >= LIMITS.CORE_MIN}        hint={`min ${LIMITS.CORE_MIN}`} />
                <GroupPill label="BOOST"       value={totals.BOOST}       ok={totals.BOOST <= LIMITS.BOOST_MAX}      hint={`max ${LIMITS.BOOST_MAX}`} />
                <GroupPill label="OPS"         value={totals.OPERATIONAL} ok={totals.OPERATIONAL <= LIMITS.OPERATIONAL_MAX} hint={`max ${LIMITS.OPERATIONAL_MAX}`} />
                <div className={`px-1.5 py-0.5 rounded font-semibold ${
                  totals.ALL === 100 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  'bg-red-50 text-red-700 border border-red-200'
                }`} data-testid="rp-total-indicator">
                  Σ {totals.ALL} / 100
                </div>
              </div>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-12 items-center gap-2 px-2.5 py-1 bg-slate-50/60 border-b border-slate-100 text-[9px] uppercase tracking-wider text-slate-400">
              <div className="col-span-4">Signal</div>
              <div className="col-span-1 text-center">Type</div>
              <div className="col-span-4">Weight</div>
              <div className="col-span-2 text-right">Value</div>
              <div className="col-span-1 text-right"></div>
            </div>

            <div className="divide-y divide-slate-100" data-testid="rp-selected-list">
              {selected.length === 0 && (
                <div className="px-3 py-6 text-center text-[11px] text-slate-400">
                  No signals selected. Click <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500 font-medium"><Plus className="h-2.5 w-2.5" />Add</span> on a catalog row (left) to include it in the contract.
                </div>
              )}
              {selected.map(row => {
                const meta = catalogByKey[row.key];
                if (!meta) return null;
                const Icon = TYPE_STYLE[meta.type].icon;
                const isCoreLast = meta.type === 'CORE' && coreSelectedCount <= 1;
                return (
                  <div key={row.key} className="grid grid-cols-12 items-center gap-2 px-2.5 py-1 hover:bg-slate-50/60" data-testid={`rp-row-${row.key}`}>
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-1">
                        <Icon className="h-3 w-3 text-slate-400 flex-shrink-0" />
                        <span className="text-[11.5px] font-medium text-[#122033] truncate">{meta.name}</span>
                      </div>
                      <p className="text-[9.5px] text-slate-400 truncate" title={meta.source}>Source: {meta.source}</p>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className={`text-[9px] px-1 py-0 rounded ${TYPE_STYLE[meta.type].chip}`}>{meta.type}</span>
                    </div>
                    <div className="col-span-4">
                      <input
                        type="range"
                        min="0" max="100" step="1"
                        value={row.weight}
                        onChange={(e) => setWeight(row.key, e.target.value)}
                        className="w-full accent-violet-500"
                        data-testid={`rp-slider-${row.key}`}
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-1">
                      <input
                        type="number" min="0" max="100"
                        value={row.weight}
                        onChange={(e) => setWeight(row.key, e.target.value)}
                        className="w-12 text-right border border-slate-200 rounded px-1 py-0.5 text-[11px] font-mono focus:ring-1 focus:ring-violet-300 focus:outline-none"
                        data-testid={`rp-input-${row.key}`}
                      />
                      <span className="text-[9px] text-slate-400">%</span>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        title={isCoreLast ? 'At least one CORE signal is required' : 'Remove'}
                        onClick={() => removeSignal(row.key)}
                        disabled={isCoreLast}
                        className={`h-5 w-5 flex items-center justify-center rounded ${isCoreLast ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:bg-red-50 hover:text-red-500'}`}
                        data-testid={`rp-remove-${row.key}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* ── Lower row: Gates | Distribution | Reset ──────── */}
        <div className="grid grid-cols-12 gap-2 mt-2">

          {/* Section 3 — Gate Rules (static system rules) */}
          <section className="col-span-12 lg:col-span-6 bg-white border border-slate-200 rounded-md" data-testid="rp-gates">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/40">
              <h2 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Gate Rules</h2>
              <Badge className="text-[9px] bg-slate-100 text-slate-500 border border-slate-200">Read-only</Badge>
            </div>
            <div className="divide-y divide-slate-100">
              {GATE_RULES.map(g => (
                <div key={g.key} className="px-2.5 py-1 flex items-start gap-1.5" data-testid={`rp-gate-${g.key}`}>
                  <Lock className="h-3 w-3 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11.5px] font-medium text-[#122033]">{g.title}</span>
                      <span className="text-[9px] px-1 py-0 rounded bg-slate-50 text-slate-500 border border-slate-200">{g.label}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{g.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4 — Weight Distribution (derived from UI inputs only) */}
          <section className="col-span-12 lg:col-span-4 bg-white border border-slate-200 rounded-md" data-testid="rp-distribution">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/40">
              <h2 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Weight Distribution</h2>
              <span className="text-[9px] text-slate-400">by type</span>
            </div>
            <div className="px-2.5 py-2">
              <StackedBar totals={totals} />
              <div className="mt-2 space-y-1">
                <LegendRow color="bg-violet-500" label="CORE"        value={totals.CORE}        total={totals.ALL} />
                <LegendRow color="bg-amber-400"  label="BOOST"       value={totals.BOOST}       total={totals.ALL} />
                <LegendRow color="bg-slate-400"  label="OPERATIONAL" value={totals.OPERATIONAL} total={totals.ALL} />
              </div>
            </div>
          </section>

          {/* Section 5 — Reset only (no fake Duplicate/Export/Import/Publish) */}
          <section className="col-span-12 lg:col-span-2 bg-white border border-slate-200 rounded-md" data-testid="rp-actions">
            <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-100 bg-slate-50/40">
              <h2 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Local Actions</h2>
            </div>
            <div className="p-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 text-[10.5px] gap-1 justify-start px-2"
                onClick={resetDefault}
                data-testid="rp-reset"
              >
                <RotateCcw className="h-3 w-3" /> Reset to Default
              </Button>
              <p className="text-[9px] text-slate-400 mt-1.5 leading-snug">
                Resets local weights only. Nothing is saved to any backend.
              </p>
            </div>
          </section>
        </div>

        {/* Footer disclosure */}
        <p className="mt-3 text-[9.5px] text-slate-400 text-center">
          Design contract only — no data is persisted and no ranking behavior is affected.
        </p>
      </div>
    </div>
  );
}

/* ══════════ Small presentational helpers ══════════ */

function GroupPill({ label, value, ok, hint }) {
  return (
    <div
      title={hint}
      className={`px-2 py-0.5 rounded border ${ok ? 'bg-slate-50 text-slate-600 border-slate-200' : 'bg-red-50 text-red-600 border-red-200'}`}
      data-testid={`rp-group-${label.toLowerCase()}`}
    >
      <span className="text-[9px] uppercase tracking-wider mr-1">{label}</span>
      <span className="text-[11px] font-mono font-semibold">{value}</span>
    </div>
  );
}

function StackedBar({ totals }) {
  const sum = Math.max(1, totals.ALL);
  const parts = [
    { key: 'CORE', v: totals.CORE, bg: 'bg-violet-500' },
    { key: 'BOOST', v: totals.BOOST, bg: 'bg-amber-400' },
    { key: 'OPERATIONAL', v: totals.OPERATIONAL, bg: 'bg-slate-400' },
  ];
  return (
    <div className="w-full h-6 rounded overflow-hidden flex bg-slate-100 border border-slate-200" data-testid="rp-stacked-bar">
      {parts.map(p => (
        <div
          key={p.key}
          className={`${p.bg} h-full transition-all`}
          style={{ width: `${(p.v / sum) * 100}%` }}
          title={`${p.key}: ${p.v}`}
          data-testid={`rp-bar-${p.key.toLowerCase()}`}
        />
      ))}
    </div>
  );
}

function LegendRow({ color, label, value, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`inline-block h-2 w-2 rounded-sm ${color}`} />
      <span className="text-slate-500 flex-1">{label}</span>
      <span className="font-mono text-[#122033]">{value}</span>
      <span className="text-slate-400 w-10 text-right">{pct}%</span>
    </div>
  );
}
